import type { MouseEventHandler } from 'react';
import React, { useState } from 'react';

interface BadgeProps {
  type?: 'solid' | 'outline';
  mode: 'error' | 'warning' | 'success' | 'info';
  size?: 'small' | 'medium' | 'large';
  text: string | JSX.Element;
  onClick?: MouseEventHandler<HTMLSpanElement>;
  dismissible?: boolean;
  className?: string; // Additional classes passed from parent component
  textAlign?: 'left' | 'center' | 'right'; // Text alignment options
}

function Badge({
  type = 'solid', mode, size = 'medium', text, onClick, dismissible = false, className = '', textAlign = 'center',
}: BadgeProps) {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) {
    return null;
  }

  const baseClass = 'inline-flex justify-between items-center px-5 py-2 rounded-lg font-medium';

  const typeClass = {
    solid: '',
    outline: {
      error: 'border border-red-400',
      warning: 'border border-orange-400',
      success: 'border border-green-400',
      info: 'border border-blue-400',
    },
  };

  const modeClass = {
    error: 'text-red-800 bg-red-100',
    warning: 'text-orange-800 bg-orange-100',
    success: 'text-green-900 bg-green-100',
    info: 'text-blue-800 bg-blue-100',
  };

  const sizeClass = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg',
  };

  const textAlignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const combinedClass = `${baseClass} ${type === 'outline' ? typeClass.outline[mode] : ''} ${modeClass[mode]} ${sizeClass[size]} ${textAlignClass[textAlign]} ${onClick ? 'cursor-pointer' : ''} ${className}`;

  return (
    <span className={combinedClass} onClick={onClick}>
      {text}
      {dismissible && (
        <button
          className="ml-2 text-gray-400 hover:text-gray-500 focus:outline-none"
          onClick={() => setIsVisible(false)}
        >
          &times;
        </button>
      )}
    </span>
  );
}

export default Badge;
